/** How long after the window `load` event deferred work waits by default. */
export const AFTER_PAGE_LOAD_DELAY_MS = 3000;

/**
 * Runs `callback` once the page has finished loading, plus a short delay.
 *
 * For third-party scripts nobody needs in the first few seconds (support
 * chat, payments). Loaded any earlier they compete with our own bundle and the
 * first data requests for bandwidth — on the calendar, Stripe and Intercom
 * together pushed the first events on screen back by roughly three seconds.
 * The delay is on top of `load` because `load` fires before the route's data
 * has arrived.
 * @param callback - Work to run after the page has loaded.
 * @param delayMs - Extra wait after the `load` event.
 * @returns A function that cancels the pending call.
 */
export function afterPageLoad(
  callback: () => void,
  delayMs: number = AFTER_PAGE_LOAD_DELAY_MS,
): () => void {
  let timer: number | undefined;
  const schedule = () => {
    timer = window.setTimeout(callback, delayMs);
  };

  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }

  return () => {
    window.removeEventListener('load', schedule);
    if (timer !== undefined) window.clearTimeout(timer);
  };
}
